import { useState, useEffect, useCallback, useMemo } from 'react';
import { Mail, Phone, Linkedin, Menu, X, Code, GraduationCap, Wrench, ChevronDown, MessageCircle, ArrowUp, Heart, Instagram, Facebook, Twitter, Github, Image, XCircle, Award, ExternalLink, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import profilePic from './assets/pp.jpg';
import { Analytics } from '@vercel/analytics/react';

export default function Portfolio() {
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [lightboxImageLoading, setLightboxImageLoading] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      setShowBackToTop(window.scrollY > 500);

      const sections = ['home', 'about', 'experience', 'projects', 'skills', 'certifications', 'gallery', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 150 && rect.bottom >= 150;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Prevent right-click and developer tools
  useEffect(() => {
    const preventRightClick = (e) => {
      e.preventDefault();
      return false;
    };

    const preventKeyShortcuts = (e) => {
      // Prevent F12 (Developer Tools)
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }

      // Prevent Ctrl+Shift+I (Developer Tools)
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        return false;
      }

      // Prevent Ctrl+Shift+J (Console)
      if (e.ctrlKey && e.shiftKey && e.key === 'J') {
        e.preventDefault();
        return false;
      }

      // Prevent Ctrl+Shift+C (Inspect Element)
      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        return false;
      }

      // Prevent Ctrl+U (View Source)
      if (e.ctrlKey && e.key === 'u') {
        e.preventDefault();
        return false;
      }

      // Prevent Ctrl+S (Save Page)
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        return false;
      }

      // Prevent Ctrl+P (Print)
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        return false;
      }
    };

    const preventTextSelection = (e) => {
      // Allow text selection in input fields and textareas
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      // Prevent text selection on other elements
      if (window.getSelection && window.getSelection().toString().length > 0) {
        return;
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', preventRightClick);
    document.addEventListener('keydown', preventKeyShortcuts);
    document.addEventListener('selectstart', preventTextSelection);
    document.addEventListener('dragstart', preventRightClick);

    // Disable right-click on images
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      img.addEventListener('contextmenu', preventRightClick);
      img.setAttribute('draggable', 'false');
    });

    return () => {
      document.removeEventListener('contextmenu', preventRightClick);
      document.removeEventListener('keydown', preventKeyShortcuts);
      document.removeEventListener('selectstart', preventTextSelection);
      document.removeEventListener('dragstart', preventRightClick);
      images.forEach(img => {
        img.removeEventListener('contextmenu', preventRightClick);
      });
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Gallery images array - defined early for use in lightbox handlers
  // Wrapped in useMemo to prevent recreation on every render
  const galleryImages = useMemo(() => [
    { id: 1, src: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/IMG_20231110_181612.jpg', alt: 'Cherished memories with best work colleagues' },
    { id: 2, src: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/IMG_1170.jpg', alt: 'Merry Christmas vibe at workplace' },
    { id: 3, src: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/IMG_1173 2.jpg', alt: 'Coding vibes at the desk' },
    { id: 4, src: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/IMG_1180.jpg', alt: 'Team collaboration and work memories' },
    { id: 5, src: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/IMG_20241111_172552.jpg', alt: 'Celebrating a decade of unforgettable moments' },
    { id: 6, src: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/IMG_20241222_095801 (1).jpg', alt: 'Breathtaking moments in the mountains of Mussoorie' },
    { id: 7, src: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/IMG_20251111_181947.jpg', alt: 'Taking on challenging tasks as a team' },
    { id: 8, src: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/IMG-20241030-WA0017.jpg', alt: 'Soaking in the festive Diwali vibes' },
    { id: 9, src: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/IMG-20241223-WA0028.jpg', alt: 'Adventures with Jaipur travel buddies' },
    { id: 10, src: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/IMG-20241223-WA0034.jpg', alt: 'Capturing perfect portraits and candid smiles' },
    { id: 11, src: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/IMG20250509102155.jpg', alt: 'Vibe of the day – moments worth remembering' },
    { id: 12, src: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/IMG_1177.jpg', alt: 'Productive coding desk setup' },
  ], []);

  // Lightbox handlers
  const openLightbox = (image) => {
    setLightboxImage(image);
    setLightboxImageLoading(true);
    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setLightboxImageLoading(false);
    // Restore body scroll
    document.body.style.overflow = '';
  };

  const navigateLightbox = useCallback((direction) => {
    setLightboxImage((currentImage) => {
      if (!currentImage || galleryImages.length === 0) return currentImage;

      const currentIndex = galleryImages.findIndex(img => img.id === currentImage.id);
      if (currentIndex === -1) return currentImage;

      let newIndex;
      if (direction === 'next') {
        newIndex = (currentIndex + 1) % galleryImages.length;
      } else {
        newIndex = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
      }

      return galleryImages[newIndex];
    });
    setLightboxImageLoading(true);
  }, [galleryImages]);

  const getCurrentImageIndex = () => {
    if (!lightboxImage || galleryImages.length === 0) return 0;
    const index = galleryImages.findIndex(img => img.id === lightboxImage.id);
    return index !== -1 ? index + 1 : 0;
  };

  // Image loading handler - optimized to prevent unnecessary re-renders
  // Improved image load handler for compatibility with mobile and web
  const handleImageLoad = (imageId) => {
    // Gracefully handle both string and number types for IDs
    setLoadedImages(prev => {
      const normalizedId = typeof imageId === 'number' ? imageId.toString() : String(imageId);
      // Mobile Safari (prone to duplicate events): check strictly for id presence
      if ([...prev].some(id => String(id) === normalizedId)) return prev;
      return new Set([...prev, normalizedId]);
    });
  };

  // Keyboard support for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxImage) {
        if (e.key === 'Escape') {
          closeLightbox();
        } else if (e.key === 'ArrowLeft') {
          navigateLightbox('prev');
        } else if (e.key === 'ArrowRight') {
          navigateLightbox('next');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxImage, navigateLightbox]);

  // Cleanup body overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Skill icons mapping - using DevIcons and Simple Icons CDN
  const skillIcons = {
    'JavaScript (ES6+)': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg',
    'TypeScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg',
    'PHP': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg',
    'HTML5': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/html5/html5-original.svg',
    'SCSS': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/sass/sass-original.svg',
    'Angular': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/angularjs/angularjs-original.svg',
    'Ionic Framework': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/ionic/ionic-original.svg',
    'React': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg',
    'Node.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg',
    'Capacitor': 'https://cdn.simpleicons.org/capacitor/119EFF',
    'Cordova': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cordova/cordova-original.svg',
    'Angular Signals': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/angularjs/angularjs-original.svg',
    'RxJS Observables': 'https://cdn.simpleicons.org/reactivex/FF1493',
    'NgRx': 'https://cdn.simpleicons.org/ngrx/8626E9',
    'Git': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/git/git-original.svg',
    'Android Studio': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/android/android-original.svg',
    'Xcode': 'https://cdn.simpleicons.org/xcode/147EFB',
    'Postman': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postman/postman-original.svg',
    'JIRA': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/jira/jira-original.svg',
    'Firebase': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/firebase/firebase-plain.svg',
    'Stripe': 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/stripe/stripe-original.svg',
    'RESTful APIs': 'https://cdn.simpleicons.org/json/000000',
    'Push Notifications': 'https://cdn.simpleicons.org/pushbullet/0B78E3',
    'In-App Purchases': 'https://cdn.simpleicons.org/apple/000000',
    'Google Maps API': 'https://cdn.simpleicons.org/googlemaps/4285F4',
    'App Store & Play Store Deployment': 'https://cdn.simpleicons.org/appstore/0D96F6'
  };

  const skills = {
    languages: ['JavaScript (ES6+)', 'TypeScript', 'PHP', 'HTML5', 'SCSS'],
    frameworks: ['Angular', 'Ionic Framework', 'React', 'Node.js', 'Capacitor', 'Cordova'],
    stateManagement: ['Angular Signals', 'RxJS Observables', 'NgRx'],
    tools: ['Git', 'Android Studio', 'Xcode', 'Postman', 'JIRA', 'Firebase', 'Stripe', 'RESTful APIs'],
    others: ['Push Notifications', 'In-App Purchases', 'Google Maps API', 'App Store & Play Store Deployment']
  };

  const projects = [
    {
      name: 'TruVideo',
      period: '12/2025 – Present',
      availability: 'SDKs for Android & iOS (Capacitor)',
      description: 'A video-based customer communication platform that enables businesses to record, share, and manage branded video content within mobile applications.',
      highlights: [
        'Developing and maintaining cross-platform Capacitor SDKs to enable native video and media features in hybrid apps.',
        'Building and maintaining Capacitor-based demo applications using React to showcase SDK integrations and usage.',
        'Ensuring SDK compatibility, stability, and performance across Android and iOS platforms.',
        'Collaborating with product and QA teams to deliver stable SDK releases and continuous improvements.'
      ],
      siteUrl: 'https://truvideo.gitbook.io/truvideo',
    },

    {
      name: 'The Porter Pros App',
      period: '08/2023 – 12/2024',
      availability: 'Available on Play Store & App Store',
      description: 'A live app that functions like "Uber for vehicle servicing." The platform enables users to schedule service appointments for their vehicles through the app.',
      highlights: [
        'Built the hybrid mobile app using Ionic Angular with real-time GPS tracking.',
        'Integrated Google Maps API and Mapbox for live driver navigation and tracking.',
        'Connected RESTful APIs for bookings, driver updates, and notifications.',
        'Added push notifications and ETA alerts using Firebase Cloud Messaging.'
      ],
      siteUrl: 'https://www.porterpros.com/', // Add your website URL here
      appStoreUrl: 'https://apps.apple.com/us/app/porter-pros/id6738954434', // Add your App Store URL here
      playStoreUrl: 'https://play.google.com/store/apps/details?id=io.porter.com&pcampaignid=web_share' // Add your Play Store URL here
    },
    {
      name: 'Beef Hub App',
      period: 'Support & Maintenance',
      availability: 'Available on Play Store & App Store',
      description: 'Built a comprehensive resource app for the livestock hauling and beef industry, improving accessibility to sale barns, packing houses, and feedlots.',
      highlights: [
        'Enhanced Google Maps with custom markers, fixed rendering issues, and used marker clustering for smoother performance.',
        'Optimized app speed and UX with Angular and Ionic best practices, achieving 500+ downloads.',
        'Implemented in-app purchases and subscriptions, and managed Play Store and App Store deployments.'
      ],
      siteUrl: 'https://backend.beefhubapp.com/', // Add your website URL here
      appStoreUrl: 'https://apps.apple.com/at/app/beef-hub/id6738084306?l=en-GB', // Add your App Store URL here
      playStoreUrl: 'https://play.google.com/store/apps/details?id=io.beef.hub&pcampaignid=web_share' // Add your Play Store URL here
    },
    {
      name: 'Exhibit Transfer',
      period: '07/2024 – 05/2025',
      availability: 'iOS (Private Distribution)',
      description: 'Developed a shipment management and tracking app integrating UPS and FedEx APIs, improving efficiency and reporting for shipment operations.',
      highlights: [
        'A shipment management app to track and manage parcel information in real time.',
        'Developed a hybrid app that fetches shipment data via UPS and FedEx APIs and saves to MySQL database.',
        'Implemented QR code scanning to fetch or update shipment details.',
        'Generated shipment reports in PDF format with email sharing and download options for detailed reporting.'
      ],
      siteUrl: '', // Add your website URL here
      appStoreUrl: '', // Add your App Store URL here
      playStoreUrl: '' // Add your Play Store URL here
    },
    {
      name: 'Angel Archive',
      period: '12/2024 - Present',
      availability: '',
      description: 'Built a family-focused mobile app enabling users to connect, share media, map relationships, and securely store private content in a protected vault.',
      highlights: [
        'A family-focused mobile app that lets users connect, share photos and videos, and map relationships in a private and secure environment.',
        'Built a secure vault feature for storing personal media and documents safely, using Ionic, Angular, and Firebase for reliable performance and data protection.',
      ],
      siteUrl: '', // Add your website URL here
      appStoreUrl: '', // Add your App Store URL here
      playStoreUrl: '' // Add your Play Store URL here
    },

  ];

  const certifications = [
    // Add your certifications here
    // Example:
    {
      name: 'Single Page Web Applications with AngularJS',
      issuer: 'Meta',
      date: '11/2025',
      credentialId: 'ZP4OL8ID907M',
      image: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/CERTIFICATE_LANDING_PAGE~02S6AC33WZJX.jpg',
      credentialUrl: 'https://www.coursera.org/account/accomplishments/verify/ZP4OL8ID907M'
    },
    {
      name: 'React Basics',
      issuer: 'Meta',
      date: '10/2025',
      credentialId: '02S6AC33WZJX',
      image: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/CERTIFICATE_LANDING_PAGE~02S6AC33WZJX.jpg',
      credentialUrl: 'https://www.coursera.org/account/accomplishments/verify/02S6AC33WZJX'
    },
    {
      name: 'Programming with JavaScript',
      issuer: 'Meta',
      date: '10/2025',
      image: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/CERTIFICATE_LANDING_PAGE~LWDNLVWH2291.jpg',
      credentialId: 'LWDNLVWH2291',
      credentialUrl: 'https://www.coursera.org/account/accomplishments/verify/LWDNLVWH2291'
    },
    {
      name: 'Front-End Development',
      issuer: 'Meta',
      date: '11/2025',
      image: 'https://cdn.jsdelivr.net/gh/Akash-E01/my-gallery-images/CERTIFICATE_LANDING_PAGE~N07PXEP9ZXHN.jpg',
      credentialId: 'N07PXEP9ZXHN',
      credentialUrl: 'https://www.coursera.org/account/accomplishments/verify/N07PXEP9ZXHN'
    }
  ];

  const navItems = ['home', 'about', 'experience', 'projects', 'skills', 'certifications', 'gallery', 'contact'];

  return (
    <div className="portfolio-container">

      {/* Animated Background */}
      <div className="animated-bg">
        <div className="grid-pattern"></div>
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <div className="orb orb-3"></div>
        <div className="particles">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 15}s`,
                animationDuration: `${10 + Math.random() * 10}s`
              }}
            ></div>
          ))}
        </div>
      </div>

      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <div className="logo">AG</div>

          <div className="nav-links">
            {navItems.map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className={`nav-button ${activeSection === item ? 'nav-button-active' : ''}`}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </button>
            ))}
          </div>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="mobile-menu">
            {navItems.map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className={`mobile-menu-item ${activeSection === item ? 'nav-button-active' : ''}`}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="section">
        <div className="max-width" style={{ textAlign: 'center' }}>
          <div className="hero-avatar">
            <div className="hero-avatar-inner">
              <img src={profilePic} alt="Akash Gupta" />
            </div>
          </div>
          <h1 className="hero-title">Akash Gupta</h1>
          <p className="hero-subtitle">Software Engineer</p>
          <p className="hero-description">
            Specializing in hybrid app development, creating seamless solutions for iOS, Android, PWAs, and modern web applications
          </p>

          <div className="button-group">
            <a href="mailto:akashguptathegre8@gmail.com" className="primary-btn">
              <Mail size={20} />
              Email Me
            </a>
            <a
              href={`https://wa.me/917275795475?text=${encodeURIComponent("Hi Akash, I'd like to get in touch regarding your portfolio.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-btn"
            >
              <MessageCircle size={20} />
              WhatsApp
            </a>
            <a href="https://www.linkedin.com/in/akash5699" target="_blank" rel="noopener noreferrer" className="secondary-btn">
              <Linkedin size={20} />
              LinkedIn
            </a>
          </div>

          <button
            onClick={() => scrollToSection('about')}
            className="bounce"
            style={{ background: 'transparent', border: 'none', color: '#8b5cf6', cursor: 'pointer' }}
          >
            <ChevronDown size={36} />
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section">
        <div className="max-width">
          <h2 className="heading">About Me</h2>
          <div className="card">
            <p style={{ fontSize: '1.2rem', color: '#d4d4d8', lineHeight: '2', marginBottom: '2.5rem' }}>
              Software Developer specializing in hybrid app development, creating seamless solutions for iOS, Android, PWAs, and
              modern web applications. Skilled in delivering cross-platform applications using Ionic and Angular, with strong integration
              of emerging technologies to improve performance and user experience.
            </p>
            <div className="grid grid-cols-2">
              <div className="info-item">
                <Phone className="info-icon" size={22} />
                <div>
                  <p style={{ color: '#a1a1aa', fontSize: '0.9rem', marginBottom: '0.4rem' }}>Phone</p>
                  <p style={{ color: '#f4f4f5', fontWeight: 500 }}>+91-7275795475</p>
                </div>
              </div>
              <div className="info-item">
                <Mail className="info-icon" size={22} />
                <div>
                  <p style={{ color: '#a1a1aa', fontSize: '0.9rem', marginBottom: '0.4rem' }}>Email</p>
                  <p style={{ color: '#f4f4f5', wordBreak: 'break-all', fontWeight: 500 }}>akashguptathegre8@gmail.com</p>
                </div>
              </div>
              <div className="info-item">
                <GraduationCap className="info-icon" size={22} />
                <div>
                  <p style={{ color: '#a1a1aa', fontSize: '0.9rem', marginBottom: '0.4rem' }}>Education</p>
                  <p style={{ color: '#f4f4f5', fontWeight: 500 }}>BCA - VBS Purvanchal University</p>
                  <p style={{ color: '#a78bfa', fontSize: '0.85rem', marginTop: '0.2rem' }}>2020 – 2023</p>
                </div>
              </div>
              <div className="info-item">
                <Linkedin className="info-icon" size={22} />
                <div>
                  <p style={{ color: '#a1a1aa', fontSize: '0.9rem', marginBottom: '0.4rem' }}>LinkedIn</p>
                  <a href="https://www.linkedin.com/in/akash5699" target="_blank" rel="noopener noreferrer" style={{ color: '#a78bfa', textDecoration: 'none', fontWeight: 500 }}>
                    /in/akash5699
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="section">
        <div className="max-width">
          <h2 className="heading">Experience</h2>
          <div className="grid">
            <div className="card">
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.9rem', fontWeight: 'bold', color: '#a78bfa', marginBottom: '0.7rem' }}>
                  Software Engineer
                </h3>
                <p style={{ fontSize: '1.2rem', color: '#f4f4f5', fontWeight: 500 }}>Five Exceptions Software Solutions Pvt Ltd</p>
                <p style={{ color: '#d4d4d8', marginTop: '0.3rem' }}>Indore, India</p>
                <p style={{ color: '#818cf8', marginTop: '0.7rem', fontWeight: 600, fontSize: '1.05rem' }}>Dec 2025 – Present</p>
              </div>
              <ul style={{ listStyle: 'none' }}>
                <li className="list-item">
                  <span className="list-marker">▹</span>
                  <span>Responsible for designing, developing, and maintaining cross-platform Capacitor SDKs enabling native functionality for hybrid mobile applications.</span>
                </li>
                <li className="list-item">
                  <span className="list-marker">▹</span>
                  <span>Build and maintain demo applications using Capacitor and React to demonstrate SDK capabilities, integrations, and best practices.</span>
                </li>
                <li className="list-item">
                  <span className="list-marker">▹</span>
                  <span>Ensure seamless compatibility, stability, and performance of SDKs across Android and iOS platforms.</span>
                </li>
                <li className="list-item">
                  <span className="list-marker">▹</span>
                  <span>Collaborate closely with product, QA, and engineering teams to deliver reliable SDK releases and continuous feature enhancements.</span>
                </li>
                <li className="list-item">
                  <span className="list-marker">▹</span>
                  <span>Support SDK consumers by improving documentation, resolving integration issues, and optimizing developer experience.</span>
                </li>
              </ul>
            </div>

            <div className="card">
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.9rem', fontWeight: 'bold', color: '#a78bfa', marginBottom: '0.7rem' }}>
                  Associate Software Developer
                </h3>
                <p style={{ fontSize: '1.2rem', color: '#f4f4f5', fontWeight: 500 }}>Jamtech Technologies Pvt Ltd</p>
                <p style={{ color: '#d4d4d8', marginTop: '0.3rem' }}>Lucknow, India</p>
                <p style={{ color: '#818cf8', marginTop: '0.7rem', fontWeight: 600, fontSize: '1.05rem' }}>June 2023 – Nov 2025</p>
              </div>
              <ul style={{ listStyle: 'none' }}>
                <li className="list-item">
                  <span className="list-marker">▹</span>
                  <span> Delivered and maintained cross platform mobile apps and PWAs using Ionic and Angular frameworks.</span>
                </li>
                <li className="list-item">
                  <span className="list-marker">▹</span>
                  <span>I have successfully published 5+ apps to App Store and Play Store with ongoing support and updates reducing
                    delivery time by 30%.</span>
                </li>
              </ul>
            </div>


          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="section">
        <div className="max-width">
          <h2 className="heading">Projects</h2>
          <div className="grid">
            {projects.map((project, idx) => (
              <div key={idx} className="card">
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#a78bfa', marginBottom: '0.7rem' }}>
                    {project.name}
                  </h3>
                  <p style={{ fontSize: '0.9rem', color: '#a1a1aa', marginBottom: '0.4rem' }}>
                    {project.availability}
                  </p>
                  <p style={{ fontSize: '0.9rem', color: '#818cf8', fontWeight: 600 }}>
                    {project.period}
                  </p>
                </div>
                <p style={{ color: '#d4d4d8', marginBottom: '1.5rem', lineHeight: '1.8', fontSize: '1.05rem' }}>
                  {project.description}
                </p>
                <ul style={{ listStyle: 'none' }}>
                  {project.highlights.map((highlight, i) => (
                    <li key={i} className="list-item">
                      <span className="list-marker">▹</span>
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
                {(project.siteUrl || project.appStoreUrl || project.playStoreUrl) && (
                  <div className="project-links">
                    {project.siteUrl && (
                      <a
                        href={project.siteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-link-btn"
                      >
                        <Globe size={18} />
                        Website
                        <ExternalLink size={14} />
                      </a>
                    )}
                    {project.appStoreUrl && (
                      <a
                        href={project.appStoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-link-btn"
                      >
                        <img
                          src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/appstore.svg"
                          alt="App Store"
                          className="store-logo"
                        />
                        App Store
                        <ExternalLink size={14} />
                      </a>
                    )}
                    {project.playStoreUrl && (
                      <a
                        href={project.playStoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="project-link-btn"
                      >
                        <img
                          src="https://cdn.jsdelivr.net/gh/simple-icons/simple-icons@v9/icons/googleplay.svg"
                          alt="Play Store"
                          className="store-logo"
                        />
                        Play Store
                        <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="section">
        <div className="max-width">
          <h2 className="heading">Skills & Technologies</h2>
          <div className="grid grid-cols-2">
            <div className="card">
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#a78bfa', marginBottom: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                <Code size={26} />
                Languages
              </h3>
              <div className="badge-container">
                {skills.languages.map((skill, idx) => (
                  <span key={idx} className="badge">
                    {skillIcons[skill] && (
                      <img src={skillIcons[skill]} alt={skill} className="badge-icon" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="card">
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#a78bfa', marginBottom: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
                <Wrench size={26} />
                Frameworks & Libraries
              </h3>
              <div className="badge-container">
                {skills.frameworks.map((skill, idx) => (
                  <span key={idx} className="badge">
                    {skillIcons[skill] && (
                      <img src={skillIcons[skill]} alt={skill} className="badge-icon" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="card">
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#a78bfa', marginBottom: '1.2rem' }}>
                State Management
              </h3>
              <div className="badge-container">
                {skills.stateManagement.map((skill, idx) => (
                  <span key={idx} className="badge">
                    {skillIcons[skill] && (
                      <img src={skillIcons[skill]} alt={skill} className="badge-icon" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="card">
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#a78bfa', marginBottom: '1.2rem' }}>
                Tools & Platforms
              </h3>
              <div className="badge-container">
                {skills.tools.map((skill, idx) => (
                  <span key={idx} className="badge">
                    {skillIcons[skill] && (
                      <img src={skillIcons[skill]} alt={skill} className="badge-icon" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            <div className="card" style={{ gridColumn: '1 / -1' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#a78bfa', marginBottom: '1.2rem' }}>
                Other Expertise
              </h3>
              <div className="badge-container">
                {skills.others.map((skill, idx) => (
                  <span key={idx} className="badge">
                    {skillIcons[skill] && (
                      <img src={skillIcons[skill]} alt={skill} className="badge-icon" onError={(e) => { e.target.style.display = 'none'; }} />
                    )}
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section id="certifications" className="section">
        <div className="max-width">
          <h2 className="heading">Certifications</h2>
          {certifications.length > 0 ? (
            <div className="grid">
              {certifications.map((cert, idx) => (
                <div key={idx} className="card">
                  <div className="certification-card-content" style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem' }}>
                    {cert.image ? (
                      <div className="certification-image" style={{
                        width: '200px',
                        minWidth: '200px',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        background: 'rgba(139, 92, 246, 0.08)',
                        border: '1px solid rgba(139, 92, 246, 0.25)',
                        cursor: cert.credentialUrl ? 'pointer' : 'default',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        flexShrink: 0
                      }}
                        onClick={() => cert.credentialUrl && window.open(cert.credentialUrl, '_blank', 'noopener,noreferrer')}
                        onMouseEnter={(e) => {
                          if (cert.credentialUrl) {
                            e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.6)';
                            e.currentTarget.style.transform = 'scale(1.03)';
                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(139, 92, 246, 0.3)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (cert.credentialUrl) {
                            e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.25)';
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        <img
                          src={cert.image}
                          alt={`${cert.name} Certificate`}
                          style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            objectFit: 'contain'
                          }}
                        />
                        {cert.credentialUrl && (
                          <div style={{
                            position: 'absolute',
                            top: '0.5rem',
                            right: '0.5rem',
                            background: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(10px)',
                            padding: '0.3rem 0.6rem',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            fontSize: '0.75rem',
                            color: '#a78bfa',
                            pointerEvents: 'none'
                          }}>
                            <ArrowUp size={12} style={{ transform: 'rotate(45deg)' }} />
                            <span>Click</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.25) 0%, rgba(99, 102, 241, 0.25) 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        border: '1px solid rgba(139, 92, 246, 0.35)'
                      }}>
                        <Award size={30} style={{ color: '#a78bfa' }} />
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <h3 className="certification-title" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#a78bfa', marginBottom: '0.5rem' }}>
                        {cert.name}
                      </h3>
                      <p className="certification-issuer" style={{ fontSize: '1.1rem', color: '#f4f4f5', fontWeight: 500, marginBottom: '0.5rem' }}>
                        {cert.issuer}
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', marginTop: '0.8rem' }}>
                        <p style={{ fontSize: '0.95rem', color: '#818cf8', fontWeight: 600 }}>
                          Issued: {cert.date}
                        </p>
                        {cert.credentialId && (
                          <p style={{ fontSize: '0.9rem', color: '#d4d4d8' }}>
                            Credential ID: <span style={{ color: '#a78bfa', fontFamily: 'monospace' }}>{cert.credentialId}</span>
                          </p>
                        )}
                        {cert.credentialUrl && (
                          <a
                            href={cert.credentialUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '0.9rem',
                              color: '#a78bfa',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.4rem',
                              padding: '0.5rem 1rem',
                              borderRadius: '10px',
                              background: 'rgba(139, 92, 246, 0.12)',
                              border: '1px solid rgba(139, 92, 246, 0.3)',
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                              position: 'relative',
                              zIndex: 10,
                              pointerEvents: 'auto'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(139, 92, 246, 0.25)';
                              e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.5)';
                              e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                              e.currentTarget.style.boxShadow = '0 6px 20px rgba(139, 92, 246, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(139, 92, 246, 0.12)';
                              e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                              e.currentTarget.style.transform = 'translateY(0) scale(1)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            Verify Credential
                            <ArrowUp size={14} style={{ transform: 'rotate(45deg)' }} />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card">
              <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                <Award size={64} style={{ color: '#a78bfa', opacity: 0.5, marginBottom: '1.5rem' }} />
                <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: '#d4d4d8' }}>No certifications added yet</p>
                <p style={{ fontSize: '0.9rem', color: '#a1a1aa', marginTop: '1rem' }}>
                  Add your certifications to the <code style={{ color: '#a78bfa', background: 'rgba(139, 92, 246, 0.12)', padding: '0.2rem 0.5rem', borderRadius: '6px' }}>certifications</code> array in App.js
                </p>
                <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(139, 92, 246, 0.05)', borderRadius: '8px', textAlign: 'left', maxWidth: '600px', margin: '1.5rem auto 0' }}>
                  <p style={{ fontSize: '0.85rem', color: '#a1a1aa', marginBottom: '0.5rem' }}>Example format:</p>
                  <pre style={{ fontSize: '0.8rem', color: '#a78bfa', overflow: 'auto', margin: 0 }}>
                    {`{
                            name: 'Certification Name',
                            issuer: 'Issuing Organization',
                            date: 'MM/YYYY',
                            image: imageVariable, // Import image and add here
                            credentialId: 'ID-12345 (optional)',
                            credentialUrl: 'https://verify-url.com (optional)'
                          }`}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="section">
        <div className="max-width">
          <h2 className="heading">Gallery</h2>
          {galleryImages.length > 0 ? (
            <div className="gallery-grid">
              {galleryImages.map((image) => {
                const isLoaded = loadedImages.has(String(image.id));
                return (
                  <div
                    key={image.id}
                    className={`gallery-item ${!isLoaded ? 'loading' : ''}`}
                    onClick={() => openLightbox(image)}
                  >
                    <img
                      src={image.src}
                      alt={image.alt || `Gallery Image ${image.id}`}
                      loading="lazy"
                      decoding="async"
                      onLoad={(e) => {
                        // Defensive: Only trigger when image is truly loaded and visible
                        if (e.target.complete && e.target.naturalWidth !== 0) {
                          handleImageLoad(image.id);
                        }
                      }}
                      style={{ opacity: isLoaded ? 1 : 0, transition: 'opacity 0.4s ease' }}
                    />
                    {!isLoaded && (
                      <div className="gallery-loader">
                        <div className="gallery-spinner"></div>
                      </div>
                    )}
                    {isLoaded && (
                      <div className="gallery-overlay">
                        <p className="gallery-overlay-text">
                          {image.alt || `Image ${image.id}`}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="card">
              <div className="gallery-placeholder">
                <Image size={64} />
                <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: '#d4d4d8' }}>No images yet</p>
                <p style={{ fontSize: '0.9rem', color: '#a1a1aa' }}>
                  Add your images to the <code style={{ color: '#a78bfa', background: 'rgba(139, 92, 246, 0.12)', padding: '0.2rem 0.5rem', borderRadius: '6px' }}>galleryImages</code> array in App.js
                </p>
                <p style={{ fontSize: '0.85rem', color: '#a1a1aa', marginTop: '1rem' }}>
                  Import images from <code style={{ color: '#a78bfa', background: 'rgba(139, 92, 246, 0.12)', padding: '0.2rem 0.5rem', borderRadius: '6px' }}>src/assets</code> folder
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="lightbox active" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
              <XCircle size={24} />
            </button>

            {galleryImages.length > 1 && (
              <>
                <button
                  className="lightbox-nav lightbox-nav-prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateLightbox('prev');
                  }}
                  aria-label="Previous image"
                >
                  <ChevronLeft size={32} />
                </button>
                <button
                  className="lightbox-nav lightbox-nav-next"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateLightbox('next');
                  }}
                  aria-label="Next image"
                >
                  <ChevronRight size={32} />
                </button>
              </>
            )}

            <div className="lightbox-image-wrapper">
              {lightboxImageLoading && (
                <div className="lightbox-loader">
                  <div className="lightbox-spinner"></div>
                </div>
              )}
              <img
                src={lightboxImage.src}
                alt={lightboxImage.alt || 'Gallery Image'}
                onLoad={() => setLightboxImageLoading(false)}
                onError={() => setLightboxImageLoading(false)}
                style={{ opacity: lightboxImageLoading ? 0 : 1, transition: 'opacity 0.3s ease' }}
              />
            </div>

            {lightboxImage.alt && (
              <div className="lightbox-caption">
                <p>{lightboxImage.alt}</p>
                {galleryImages.length > 1 && (
                  <span className="lightbox-counter">
                    {getCurrentImageIndex()} / {galleryImages.length}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contact Section */}
      <section id="contact" className="section">
        <div className="max-width" style={{ maxWidth: '800px', textAlign: 'center' }}>
          <h2 className="heading">Get In Touch</h2>
          <p style={{ fontSize: '1.35rem', color: '#d4d4d8', marginBottom: '3.5rem', lineHeight: '2' }}>
            I'm always open to discussing new projects, creative ideas, or opportunities to be part of your vision. Feel free to reach out!
          </p>
          <div className="button-group">
            <a href="mailto:akashguptathegre8@gmail.com" className="primary-btn" style={{ fontSize: '1.15rem', padding: '1.2rem 2.8rem' }}>
              <Mail size={22} />
              Send Email
            </a>
            <a href={`https://wa.me/917275795475?text=${encodeURIComponent("Hi Akash, I'd like to get in touch regarding your portfolio.")}`} target="_blank" rel="noopener noreferrer" className="whatsapp-btn">
              <MessageCircle size={20} />
              WhatsApp
            </a>
            <a href="https://www.linkedin.com/in/akash5699" target="_blank" rel="noopener noreferrer" className="secondary-btn" style={{ fontSize: '1.15rem', padding: '1.2rem 2.8rem' }}>
              <Linkedin size={22} />
              Connect on LinkedIn
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-social">
            <a
              href="mailto:akashguptathegre8@gmail.com"
              className="footer-social-link"
              aria-label="Email"
            >
              <Mail size={22} />
            </a>
            <a
              href="https://github.com/Akash-E01"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              aria-label="GitHub"
            >
              <Github size={22} />
            </a>
            <a
              href="https://www.linkedin.com/in/akash5699"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              aria-label="LinkedIn"
            >
              <Linkedin size={22} />
            </a>
            <a
              href="https://x.com/Akashgupta011"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              aria-label="Twitter"
            >
              <Twitter size={22} />
            </a>
            <a
              href="https://www.instagram.com/akashgupta5699/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              aria-label="Instagram"
            >
              <Instagram size={22} />
            </a>
            <a
              href="https://www.facebook.com/akash5699"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              aria-label="Facebook"
            >
              <Facebook size={22} />
            </a>
            <a
              href={`https://wa.me/917275795475?text=${encodeURIComponent("Hi Akash, I'd like to get in touch regarding your portfolio.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              aria-label="WhatsApp"
            >
              <MessageCircle size={22} />
            </a>
          </div>

          <div className="footer-divider"></div>

          <div className="footer-text">
            <p className="footer-copyright">
              © {new Date().getFullYear()} Akash Gupta. All rights reserved.
            </p>
            <p className="footer-built">
              Built with <Heart size={14} style={{ color: '#a78bfa', fill: '#a78bfa' }} /> and <span>React.js</span>
            </p>
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      <button
        className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <ArrowUp size={24} />
      </button>
      <Analytics />
    </div>
  );
}